// Mirrors ds-lidar-pipeline's process/src/clearance/{models,operators,rules}.py
// and process/src/variables/{variables,span_variables}.py - the pipeline's own
// Pydantic model and registered-variable list are the source of truth; this
// is a hand-kept TS mirror of the guided wizard's subset (recursive all/any
// condition groups, same as the pipeline's own Condition model).

export const OPERATORS = [
  "less_than",
  "less_than_or_equal_to",
  "greater_than",
  "greater_than_or_equal_to",
  "equal_to",
] as const;
export type Operator = (typeof OPERATORS)[number];

// classification is a category, not an ordinal distance - the pipeline only
// ever compares it for equality (see LeafConditionRow).
const EQUAL_TO_ONLY: readonly Operator[] = ["equal_to"];

// The pipeline's registered condition variables (physical measurements it
// computes per point) - see variables/variables.py and
// variables/span_variables.py's @variable(...) registrations - plus
// `classification`, a raw LAS column rather than a registered variable (see
// models.py's _KNOWN_RAW_COLUMNS) but just as legitimate a condition target.
// Each carries its value widget (`valueKind`) and allowed operators, so a
// leaf condition's UI doesn't special-case a variable by name.
// `classification`'s own values come from the region's lidar_type table
// (see LeafConditionRow), not a static list here.
export const VARIABLES = [
  { name: "powerline_distance", valueKind: "number", operators: OPERATORS },
  { name: "powerline_distance_horizontal", valueKind: "number", operators: OPERATORS },
  { name: "powerline_distance_vertical", valueKind: "number", operators: OPERATORS },
  { name: "height_above_ground", valueKind: "number", operators: OPERATORS },
  { name: "position_along_span", valueKind: "number", operators: OPERATORS },
  { name: "span_length", valueKind: "number", operators: OPERATORS },
  { name: "centre_line_distance", valueKind: "number", operators: OPERATORS },
  { name: "centre_line_distance_horizontal", valueKind: "number", operators: OPERATORS },
  { name: "centre_line_distance_vertical", valueKind: "number", operators: OPERATORS },
  { name: "outer_phase_distance_horizontal", valueKind: "number", operators: OPERATORS },
  { name: "classification", valueKind: "enum", operators: EQUAL_TO_ONLY },
] as const;
export type Variable = (typeof VARIABLES)[number]["name"];

export function variableSpec(variable: Variable) {
  return VARIABLES.find((spec) => spec.name === variable)!;
}

function isVariable(name: string): name is Variable {
  return (VARIABLES as readonly { name: string }[]).some((spec) => spec.name === name);
}

export type Combinator = "all" | "any";

// A value edited in the wizard: either a literal number, or a reference to a
// named parameter declared in the Parameters section (see WizardParameter) -
// the pipeline's own "$NAME" placeholder substitution (clearance/models.py's
// _substitute_parameters), which only ever applies within `rules` (never
// entity_checks) - so a leaf condition's value and a rule action's severity
// use this; risk tier levels don't.
export type WizardValue = { kind: "literal"; value: number | null } | { kind: "param"; name: string };

export function literalValue(value: number | null = null): WizardValue {
  return { kind: "literal", value };
}

function isParamToken(value: unknown): value is string {
  return typeof value === "string" && /^\$[A-Za-z0-9_]+$/.test(value);
}

function buildValue(value: WizardValue): number | string | null {
  return value.kind === "param" ? `$${value.name}` : value.value;
}

function parseValue(raw: unknown): WizardValue | null {
  if (typeof raw === "number") return literalValue(raw);
  if (isParamToken(raw)) return { kind: "param", name: raw.slice(1) };
  return null;
}

// One leaf condition being edited - `value` is an empty literal while the
// admin hasn't filled it in yet, which drops it (and its enclosing group, if
// it's the only child) from the generated config rather than emitting an
// incomplete condition.
export type LeafCondition = {
  key: string;
  kind: "leaf";
  variable: Variable;
  operator: Operator;
  value: WizardValue;
};

export function newLeafCondition(): LeafCondition {
  return {
    key: crypto.randomUUID(),
    kind: "leaf",
    variable: VARIABLES[0].name,
    operator: "less_than_or_equal_to",
    value: literalValue(),
  };
}

// A nested all/any group - the pipeline's own Condition model is
// self-referential (each child of "all"/"any" is itself a full Condition),
// so a condition node here can be a leaf or another group, recursively.
export type GroupCondition = {
  key: string;
  kind: "group";
  combinator: Combinator;
  children: ConditionNode[];
};

export function newGroupCondition(): GroupCondition {
  return {
    key: crypto.randomUUID(),
    kind: "group",
    combinator: "all",
    children: [newLeafCondition()],
  };
}

export type ConditionNode = LeafCondition | GroupCondition;

export function updateByKey<T extends { key: string }>(
  items: T[],
  key: string,
  updated: T
): T[] {
  return items.map((item) => (item.key === key ? updated : item));
}

export function removeByKey<T extends { key: string }>(
  items: T[],
  key: string
): T[] {
  return items.filter((item) => item.key !== key);
}

// The severity value a rule sets on one axis when its conditions hold - an
// axis is only affected by a rule at all while it has an entry here (the UI
// presents this as a checkbox per axis).
export type RuleActions = Map<string, WizardValue>;

// One rule as edited in the wizard: `root` is the same recursive group shape
// as any nested group, so building its config just means running the same
// buildCondition() the nested case already uses (see buildClearanceConfig
// below) - a rule's top-level combine isn't a separate concept, it's a
// GroupCondition. It can affect several axes at once, same as the
// pipeline's own Rule model (a list of actions, not just one).
export type WizardRule = {
  key: string;
  root: GroupCondition;
  actions: RuleActions;
};

export function newWizardRule(): WizardRule {
  return { key: crypto.randomUUID(), root: newGroupCondition(), actions: new Map() };
}

function cloneConditionNode(node: ConditionNode): ConditionNode {
  return node.kind === "leaf"
    ? { ...node, key: crypto.randomUUID() }
    : { ...node, key: crypto.randomUUID(), children: node.children.map(cloneConditionNode) };
}

// A fresh, independently-editable copy of `rule` - every condition node gets
// its own new key (not just the rule itself), so editing the duplicate can
// never affect the original's React state.
export function duplicateWizardRule(rule: WizardRule): WizardRule {
  return {
    key: crypto.randomUUID(),
    root: cloneConditionNode(rule.root) as GroupCondition,
    actions: new Map(rule.actions),
  };
}

function mapConditionValues(node: ConditionNode, fn: (value: WizardValue) => WizardValue): ConditionNode {
  return node.kind === "leaf"
    ? { ...node, value: fn(node.value) }
    : { ...node, children: node.children.map((child) => mapConditionValues(child, fn)) };
}

function mapRuleValues(rules: WizardRule[], fn: (value: WizardValue) => WizardValue): WizardRule[] {
  return rules.map((rule) => ({
    ...rule,
    root: mapConditionValues(rule.root, fn) as GroupCondition,
    actions: new Map([...rule.actions].map(([axisName, value]) => [axisName, fn(value)])),
  }));
}

// Every condition/action value referencing parameter `from` (by name) is
// renamed to `to` - keeps a rule's stored reference in sync when the admin
// renames the parameter it points at, rather than leaving it pointing at a
// name that's no longer declared.
export function renameParameterReferences(rules: WizardRule[], from: string, to: string): WizardRule[] {
  return mapRuleValues(rules, (value) =>
    value.kind === "param" && value.name === from ? { kind: "param", name: to } : value
  );
}

// Every condition/action value referencing parameter `name` reverts to an
// empty literal - used when that parameter is deleted, so no rule is left
// silently referencing a name that no longer exists.
export function clearParameterReferences(rules: WizardRule[], name: string): WizardRule[] {
  return mapRuleValues(rules, (value) =>
    value.kind === "param" && value.name === name ? literalValue() : value
  );
}

export type AxisSpec = { name: string; max_value: number };
export type Condition =
  | { name: Variable; operator: Operator; value: number | string }
  | { all: Condition[] }
  | { any: Condition[] };
export type Action = {
  name: "set_infringement_class";
  params: { value: number | string; key: string };
};
export type Rule = { conditions: Condition; actions: Action[] };

// clearance/entity_checks.py's ENTITY_CHECKS registry - only "tree_risk" is
// registered today, so it's the only check the wizard offers.
export const ENTITY_CHECKS = ["tree_risk"] as const;
export type EntityCheckName = (typeof ENTITY_CHECKS)[number];

export type RiskTier = { level: number; part_will_hit_level?: number };
export type EntityCheck = {
  axis: string;
  // Points the check reports as isolated (e.g. tree_risk's lone_risk_tree
  // tier) are scored on this axis instead of `axis` when given - omitted
  // entirely (not an empty string) when unset, so they fall back to `axis`,
  // matching ds-lidar-pipeline's EntityCheck.isolated_axis default.
  isolated_axis?: string;
  check: EntityCheckName;
  // A check's params are forwarded as **kwargs to its registered function
  // (apply_entity_checks), so it can carry more than risk_tree/lone_risk_tree
  // (e.g. tree_risk's `classifications`) - allowed here even though only the
  // two tiers are editable, so an unedited extra param round-trips instead
  // of being silently dropped on save.
  params: { risk_tree?: RiskTier; lone_risk_tree?: RiskTier; [key: string]: unknown };
};

// One named constant from the pipeline's parameters.metric/non_metric block
// (clearance/models.py's _substitute_parameters) - referenced from a rule's
// condition value or action severity as "$NAME" instead of a literal number,
// so e.g. bumping one threshold shared by many rules is a single edit. A
// name must be unique across metric and non_metric combined, same as the
// pipeline's own collision check. `name` is stored without the leading "$".
export type ParameterKind = "metric" | "non_metric";
export type WizardParameter = {
  key: string;
  name: string;
  kind: ParameterKind;
  value: number;
};

export function newWizardParameter(kind: ParameterKind): WizardParameter {
  return { key: crypto.randomUUID(), name: "", kind, value: 0 };
}

export function duplicateParameterNames(parameters: WizardParameter[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const param of parameters) {
    if (param.name === "") continue;
    if (seen.has(param.name)) duplicates.add(param.name);
    seen.add(param.name);
  }
  return duplicates;
}

export type Parameters = { metric: Record<string, number>; non_metric: Record<string, number> };

export type ClearanceConfig = {
  axes: AxisSpec[];
  rules: Rule[];
  entity_checks: EntityCheck[];
  parameters?: Parameters;
};

// A leaf with no value, or a group with zero surviving children, contributes
// nothing - null propagates up so an empty group vanishes from its own
// parent rather than emitting `{all: []}`/`{any: []}` (invalid per the
// pipeline's Condition model, which requires at least one child).
function buildCondition(node: ConditionNode): Condition | null {
  if (node.kind === "leaf") {
    const value = buildValue(node.value);
    if (value === null) return null;
    return { name: node.variable, operator: node.operator, value };
  }
  const built = node.children
    .map(buildCondition)
    .filter((condition): condition is Condition => condition !== null);
  if (built.length === 0) return null;
  if (built.length === 1) return built[0];
  return node.combinator === "all" ? { all: built } : { any: built };
}

function parseCondition(condition: Condition): ConditionNode {
  if ("all" in condition) {
    return {
      key: crypto.randomUUID(),
      kind: "group",
      combinator: "all",
      children: condition.all.map(parseCondition),
    };
  }
  if ("any" in condition) {
    return {
      key: crypto.randomUUID(),
      kind: "group",
      combinator: "any",
      children: condition.any.map(parseCondition),
    };
  }
  return {
    key: crypto.randomUUID(),
    kind: "leaf",
    variable: condition.name,
    operator: condition.operator,
    value: parseValue(condition.value) ?? literalValue(),
  };
}

// The condition tree's root always parses back into a GroupCondition, even a
// rule whose only top-level condition is a single leaf - a WizardRule's root
// is always a group (see RuleCard/ConditionListEditor), so a leaf/lone-child
// tree gets wrapped in a synthetic "all" of one.
function parseConditionRoot(condition: Condition): GroupCondition {
  const parsed = parseCondition(condition);
  return parsed.kind === "group"
    ? parsed
    : { key: crypto.randomUUID(), kind: "group", combinator: "all", children: [parsed] };
}

export function parseWizardRule(rule: Rule): WizardRule {
  return {
    key: crypto.randomUUID(),
    root: parseConditionRoot(rule.conditions),
    actions: new Map(
      rule.actions.map((action) => [action.params.key, parseValue(action.params.value) ?? literalValue()])
    ),
  };
}

export function buildClearanceConfig(
  axes: AxisSpec[],
  wizardRules: WizardRule[],
  wizardEntityChecks: WizardEntityCheck[],
  wizardParameters: WizardParameter[]
): ClearanceConfig {
  const rules: Rule[] = [];
  for (const wizardRule of wizardRules) {
    const conditions = buildCondition(wizardRule.root);
    if (conditions === null) continue;

    const actions: Action[] = [];
    for (const [axisName, value] of wizardRule.actions) {
      const built = buildValue(value);
      if (built === null) continue;
      actions.push({ name: "set_infringement_class", params: { value: built, key: axisName } });
    }
    if (actions.length === 0) continue;

    rules.push({ conditions, actions });
  }

  const parameters = buildParameters(wizardParameters);
  return {
    axes,
    rules,
    entity_checks: buildEntityChecks(wizardEntityChecks),
    ...(parameters ? { parameters } : {}),
  };
}

function buildParameters(parameters: WizardParameter[]): Parameters | null {
  const metric: Record<string, number> = {};
  const non_metric: Record<string, number> = {};
  for (const param of parameters) {
    if (param.name === "") continue;
    (param.kind === "metric" ? metric : non_metric)[`$${param.name}`] = param.value;
  }
  if (Object.keys(metric).length === 0 && Object.keys(non_metric).length === 0) return null;
  return { metric, non_metric };
}

// One risk tier as edited in the wizard - `null` means the tier is omitted
// entirely (the check falls back to the other tier's own values, see
// _tier_levels in ds-lidar-pipeline's entity_checks.py); `partWillHitLevel:
// null` means it defaults to `level` rather than escalating further. Never
// parameter-referenced - the pipeline's "$NAME" substitution only ever
// applies within `rules`, not `entity_checks`.
export type WizardRiskTier = { level: number | null; partWillHitLevel: number | null };

// One entity check as edited in the wizard. At least one of riskTree/
// loneRiskTree must be filled in before it contributes to the built config -
// same "needs at least one tier" requirement _tree_risk_check enforces.
// `extraParams` holds any params beyond the two tiers (e.g. tree_risk's
// `classifications`) - not editable here, kept as-is so loading and saving
// a check that uses one doesn't silently drop it.
export type WizardEntityCheck = {
  key: string;
  axis: string;
  // "" means unset (falls back to `axis`, same as the check never getting a
  // isolated_axis at all) - kept as a plain string rather than `string | null`
  // so it round-trips through a TextField select the same way `axis` does.
  isolatedAxis: string;
  check: EntityCheckName;
  riskTree: WizardRiskTier | null;
  loneRiskTree: WizardRiskTier | null;
  extraParams: Record<string, unknown>;
};

export function newWizardEntityCheck(): WizardEntityCheck {
  return {
    key: crypto.randomUUID(),
    axis: "",
    isolatedAxis: "",
    check: "tree_risk",
    riskTree: { level: null, partWillHitLevel: null },
    loneRiskTree: null,
    extraParams: {},
  };
}

function buildTier(tier: WizardRiskTier | null): RiskTier | null {
  if (tier === null || tier.level === null) return null;
  return tier.partWillHitLevel === null
    ? { level: tier.level }
    : { level: tier.level, part_will_hit_level: tier.partWillHitLevel };
}

function parseTier(tier: RiskTier | undefined): WizardRiskTier | null {
  if (tier === undefined) return null;
  return { level: tier.level, partWillHitLevel: tier.part_will_hit_level ?? null };
}

export function buildEntityChecks(checks: WizardEntityCheck[]): EntityCheck[] {
  const built: EntityCheck[] = [];
  for (const check of checks) {
    if (!check.axis) continue;
    const riskTree = buildTier(check.riskTree);
    const loneRiskTree = buildTier(check.loneRiskTree);
    if (!riskTree && !loneRiskTree) continue;

    const params: EntityCheck["params"] = { ...check.extraParams };
    if (riskTree) params.risk_tree = riskTree;
    if (loneRiskTree) params.lone_risk_tree = loneRiskTree;
    built.push({
      axis: check.axis,
      // Only while lone_risk_tree itself is actually set - the isolated axis
      // picker is disabled in the UI whenever it isn't, so a value entered
      // before Lone risk tree was unchecked shouldn't silently survive here.
      ...(check.isolatedAxis && loneRiskTree ? { isolated_axis: check.isolatedAxis } : {}),
      check: check.check,
      params,
    });
  }
  return built;
}

export function parseWizardEntityCheck(check: EntityCheck): WizardEntityCheck {
  const { risk_tree, lone_risk_tree, ...extraParams } = check.params;
  return {
    key: crypto.randomUUID(),
    axis: check.axis,
    isolatedAxis: check.isolated_axis ?? "",
    check: check.check,
    riskTree: parseTier(risk_tree),
    loneRiskTree: parseTier(lone_risk_tree),
    extraParams,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isCondition(value: unknown): value is Condition {
  if (!isRecord(value)) return false;
  if (Array.isArray(value.all)) return value.all.length > 0 && value.all.every(isCondition);
  if (Array.isArray(value.any)) return value.any.length > 0 && value.any.every(isCondition);
  return (
    typeof value.name === "string" &&
    isVariable(value.name) &&
    typeof value.operator === "string" &&
    (OPERATORS as readonly string[]).includes(value.operator) &&
    (typeof value.value === "number" || isParamToken(value.value))
  );
}

function isAction(value: unknown): value is Action {
  if (!isRecord(value) || value.name !== "set_infringement_class") return false;
  if (!isRecord(value.params)) return false;
  return (
    typeof value.params.key === "string" &&
    (typeof value.params.value === "number" || isParamToken(value.params.value))
  );
}

// Structural validation for one stored rule - guards parseStoredConfig
// against a config that's missing fields, shaped unexpectedly, or otherwise
// not what this wizard produced (e.g. hand-edited, or written by a future/
// past version of this schema). An invalid rule is skipped rather than
// thrown, so one bad entry can't block loading the rest of a system's config.
function isRule(value: unknown): value is Rule {
  if (!isRecord(value)) return false;
  return (
    isCondition(value.conditions) &&
    Array.isArray(value.actions) &&
    value.actions.length > 0 &&
    value.actions.every(isAction)
  );
}

function isRiskTier(value: unknown): value is RiskTier {
  if (!isRecord(value)) return false;
  return (
    typeof value.level === "number" &&
    (value.part_will_hit_level === undefined || typeof value.part_will_hit_level === "number")
  );
}

function isEntityCheck(value: unknown): value is EntityCheck {
  if (!isRecord(value)) return false;
  if (typeof value.axis !== "string") return false;
  if (value.isolated_axis !== undefined && typeof value.isolated_axis !== "string") return false;
  if (typeof value.check !== "string" || !(ENTITY_CHECKS as readonly string[]).includes(value.check)) {
    return false;
  }
  if (!isRecord(value.params)) return false;
  if (value.params.risk_tree !== undefined && !isRiskTier(value.params.risk_tree)) return false;
  if (value.params.lone_risk_tree !== undefined && !isRiskTier(value.params.lone_risk_tree)) return false;
  return true;
}

function parseParameters(value: unknown): WizardParameter[] {
  if (!isRecord(value)) return [];
  const { metric, non_metric } = value;

  const collect = (kind: ParameterKind, map: unknown): WizardParameter[] => {
    if (!isRecord(map)) return [];
    return Object.entries(map)
      .filter((entry): entry is [string, number] => typeof entry[1] === "number")
      .map(([rawName, paramValue]) => ({
        key: crypto.randomUUID(),
        name: rawName.startsWith("$") ? rawName.slice(1) : rawName,
        kind,
        value: paramValue,
      }));
  };

  return [...collect("metric", metric), ...collect("non_metric", non_metric)];
}

// A system's stored config, as loosely typed on LidarClearanceSystem - parsed
// back into wizard state both when selecting a system (loading its own saved
// config) and when copying another system's config into the current draft.
// Defensive throughout: `config` (or any field on it, e.g. a missing/renamed
// `axes`, `rules` not being an array, one malformed rule) reflects whatever
// is actually stored, not necessarily what this wizard last wrote - anything
// that doesn't validate is dropped rather than thrown, and counted in
// `skippedCount` so the caller can warn instead of silently losing data.
export function parseStoredConfig(config: Record<string, unknown> | null | undefined): {
  rules: WizardRule[];
  entityChecks: WizardEntityCheck[];
  parameters: WizardParameter[];
  skippedCount: number;
} {
  const rawRules = Array.isArray(config?.rules) ? config.rules : [];
  const rawEntityChecks = Array.isArray(config?.entity_checks) ? config.entity_checks : [];

  const validRules = rawRules.filter(isRule);
  const validEntityChecks = rawEntityChecks.filter(isEntityCheck);

  return {
    rules: validRules.map(parseWizardRule),
    entityChecks: validEntityChecks.map(parseWizardEntityCheck),
    parameters: parseParameters(config?.parameters),
    skippedCount:
      rawRules.length - validRules.length + (rawEntityChecks.length - validEntityChecks.length),
  };
}
